import {
  isAddonAvailableForSize,
  resolveAddonPriceForSize,
} from "./productAddons";
import { normalizeOptionList } from "./variantStock";

function productSizesList(product) {
  return normalizeOptionList(product?.sizes);
}

export function createBundleOfferId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `bof_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultBundleOffer() {
  return {
    id: createBundleOfferId(),
    name: "",
    description: "",
    is_active: true,
    pricing_mode: "fixed",
    offer_price: "",
    discount_percent: "",
    pick_count: 3,
    required_size: "",
    eligibility_type: "products",
    product_ids: [],
    category_ids: [],
    pick_slots: syncPickSlotsCount([], 3),
    use_pick_slots: false,
  };
}

export function normalizePickSlots(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list.slice(0, 20).map((slot, idx) => {
    const label = slot?.label != null ? String(slot.label) : `العنصر ${idx + 1}`;
    const slotLegacySize = String(slot?.required_size || "").trim();
    const options = (Array.isArray(slot?.options) ? slot.options : [])
      .map((opt) => ({
        product_id: String(opt?.product_id || "").trim(),
        surcharge:
          opt?.surcharge === "" || opt?.surcharge == null
            ? ""
            : String(opt?.surcharge),
        required_size: String(opt?.required_size || slotLegacySize || "").trim(),
      }))
      .filter((opt) => opt.product_id);
    return { label, options };
  });
}

export function syncPickSlotsCount(slots, count) {
  const n = Math.max(1, Math.min(20, Number(count) || 1));
  const cur = normalizePickSlots(slots);
  const next = [...cur];
  while (next.length < n) {
    next.push({ label: `العنصر ${next.length + 1}`, options: [] });
  }
  return next.slice(0, n);
}

export function pickSlotsModeEnabled(offer) {
  if (offer?.use_pick_slots === true) return true;
  const slots = normalizePickSlots(offer?.pick_slots);
  return slots.length > 0 && slots.some((s) => s.options.length > 0);
}

/** Offer is saved/active with per-slot choices (every slot has ≥1 option). */
export function offerUsesPickSlots(offer) {
  const slots = normalizePickSlots(offer?.pick_slots);
  if (!slots.length) return false;
  return slots.every((s) => s.options.length > 0);
}

export function slotOptionRequiredSize(slot, productId) {
  const pid = String(productId || "").trim();
  const opt = (slot?.options || []).find((o) => String(o.product_id) === pid);
  if (!opt) return "";
  return String(opt?.required_size || "").trim();
}

export function pickRequiredSize(offer, slotIdx, productId = null) {
  const slots = normalizePickSlots(offer?.pick_slots);
  if (offerUsesPickSlots(offer) && slots[slotIdx]) {
    if (productId != null) {
      return slotOptionRequiredSize(slots[slotIdx], productId);
    }
    return "";
  }
  return String(offer?.required_size || "").trim();
}

export function slotOptionSurcharge(slot, productId) {
  const pid = String(productId || "").trim();
  const opt = (slot?.options || []).find((o) => String(o.product_id) === pid);
  if (!opt) return 0;
  return parsePrice(opt.surcharge, 0);
}

export function computeBundleSlotSurcharges(offer, picks) {
  const slots = normalizePickSlots(offer?.pick_slots);
  if (!offerUsesPickSlots(offer)) return 0;
  let total = 0;
  (picks || []).forEach((pick, idx) => {
    const slot = slots[idx];
    if (!slot) return;
    const fromPick = pick?.slot_surcharge;
    if (fromPick != null && fromPick !== "") {
      total += parsePrice(fromPick, 0);
      return;
    }
    total += slotOptionSurcharge(slot, pick?.product_id || pick?.productId);
  });
  return Math.round(total * 100) / 100;
}

export function normalizeBundleOffers(raw, { forEditor = false } = {}) {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((o) => {
      const id = String(o?.id || "").trim() || createBundleOfferId();
      const pickCount = Math.max(1, Math.min(20, Number(o?.pick_count) || 1));
      return {
        id,
        name: forEditor ? String(o?.name || "") : String(o?.name || "").trim(),
        description: forEditor
          ? String(o?.description || "")
          : String(o?.description || "").trim(),
        is_active: o?.is_active !== false && o?.is_active !== 0,
        pricing_mode: o?.pricing_mode === "discount" ? "discount" : "fixed",
        offer_price:
          o?.offer_price === "" || o?.offer_price == null
            ? ""
            : String(o?.offer_price),
        discount_percent:
          o?.discount_percent === "" || o?.discount_percent == null
            ? ""
            : String(o?.discount_percent),
        pick_count: pickCount,
        required_size: String(o?.required_size || "").trim(),
        eligibility_type: o?.eligibility_type === "categories" ? "categories" : "products",
        product_ids: (Array.isArray(o?.product_ids) ? o.product_ids : [])
          .map((x) => String(x || "").trim())
          .filter(Boolean),
        category_ids: (Array.isArray(o?.category_ids) ? o.category_ids : [])
          .map((x) => String(x || "").trim())
          .filter(Boolean),
        image_url: String(o?.image_url || "").trim(),
        pick_slots: normalizePickSlots(o?.pick_slots),
        use_pick_slots:
          o?.use_pick_slots === true ||
          normalizePickSlots(o?.pick_slots).some((s) => s.options.length > 0),
      };
    })
    .filter((o) => forEditor || (o.is_active && o.name));
}

export function activeBundleOffers(settings) {
  return normalizeBundleOffers(settings?.offers).filter((o) => o.is_active && o.name);
}

export function productCategoryId(product) {
  if (!product) return "";
  if (product.category_id != null && String(product.category_id).trim()) {
    return String(product.category_id).trim();
  }
  const c = product.categories;
  if (c == null) return "";
  if (typeof c === "object" && !Array.isArray(c)) return String(c.id || "").trim();
  if (Array.isArray(c) && c[0]) return String(c[0].id || c[0]).trim();
  return "";
}

export function getEligibleProductsForOffer(offer, products) {
  const list = Array.isArray(products) ? products : [];
  const type = offer?.eligibility_type === "categories" ? "categories" : "products";
  if (type === "products") {
    const ids = new Set((offer?.product_ids || []).map((x) => String(x)));
    return list.filter((p) => ids.has(String(p.id)));
  }
  const catIds = new Set((offer?.category_ids || []).map((x) => String(x)));
  return list.filter((p) => catIds.has(productCategoryId(p)));
}

function parsePrice(raw, fallback = 0) {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : fallback;
}

/** Base unit price for a product at a given size (before addons). */
export function productBasePriceForSize(product, size) {
  if (!product) return 0;
  const sz = String(size || "").trim();
  const extras = Array.isArray(product.prices)
    ? product.prices
    : Array.isArray(product.extra_prices)
      ? product.extra_prices
      : [];
  if (sz && extras.length) {
    for (const row of extras) {
      const lab = String(
        row.description_ar || row.description_en || row.label || ""
      ).trim();
      if (lab && lab.toLowerCase() === sz.toLowerCase()) {
        return parsePrice(row.price, parsePrice(product.base_price ?? product.price));
      }
    }
  }
  return parsePrice(product.base_price ?? product.price, 0);
}

export function computeBundleBasePrice(offer, picks, productsById) {
  const mode = offer?.pricing_mode === "discount" ? "discount" : "fixed";
  if (mode === "fixed") {
    return parsePrice(offer?.offer_price, 0);
  }
  const pct = Math.min(99, Math.max(1, parsePrice(offer?.discount_percent, 0)));
  let sum = 0;
  for (const pick of picks || []) {
    const p = productsById.get(String(pick.product_id || pick.productId || ""));
    const sz = pick.selected_size || pick.size || offer?.required_size || "";
    sum += productBasePriceForSize(p, sz);
  }
  return Math.round(sum * (1 - pct / 100) * 100) / 100;
}

export function computeBundleAddonsTotal(picks, productsById) {
  let total = 0;
  for (const pick of picks || []) {
    const p = productsById.get(String(pick.product_id || pick.productId || ""));
    const sz = pick.selected_size || pick.size || "";
    const sizes = Array.isArray(p?.sizes) ? p.sizes : [];
    const hasSizes = sizes.length > 0;
    const addons = Array.isArray(pick.selected_addons)
      ? pick.selected_addons
      : Array.isArray(pick.addons)
        ? pick.addons
        : [];
    for (const ad of addons) {
      const raw = (p?.addons || p?.product_addons || []).find(
        (a) => String(a.id) === String(ad.id)
      );
      if (raw) {
        const pr = resolveAddonPriceForSize(raw, sz, hasSizes);
        total += pr != null ? pr : parsePrice(ad.price);
      } else {
        total += parsePrice(ad.price);
      }
    }
  }
  return Math.round(total * 100) / 100;
}

export function computeBundleLineTotal(offer, picks, productsById) {
  const base = computeBundleBasePrice(offer, picks, productsById);
  const addons = computeBundleAddonsTotal(picks, productsById);
  const slotExtra = computeBundleSlotSurcharges(offer, picks);
  return Math.round((base + addons + slotExtra + Number.EPSILON) * 100) / 100;
}

export function normalizeBundlePickRow(row, offer, slotIdx = null) {
  const productId = String(row?.product_id || row?.productId || "").trim();
  let fallback = "";
  if (slotIdx != null && offerUsesPickSlots(offer)) {
    fallback = pickRequiredSize(offer, slotIdx, productId);
  } else {
    fallback = String(offer?.required_size || "").trim();
  }
  const size = String(row?.selected_size || row?.size || fallback).trim();
  const rawAddons = Array.isArray(row?.selected_addons)
    ? row.selected_addons
    : Array.isArray(row?.addons)
      ? row.addons
      : [];
  const selected_addons = rawAddons.map((a) => ({
    id: String(a.id || ""),
    name: String(a.name || ""),
    price: parsePrice(a.price),
  }));
  const product_name = String(row?.product_name || row?.product_name_ar || "").trim();
  const slot_surcharge =
    row?.slot_surcharge === "" || row?.slot_surcharge == null
      ? null
      : parsePrice(row.slot_surcharge, 0);
  return {
    product_id: productId,
    selected_size: size || null,
    selected_addons,
    ...(product_name ? { product_name } : {}),
    ...(slot_surcharge != null ? { slot_surcharge } : {}),
  };
}

export function enrichBundlePickRow(row, offer, productsById, slotIdx = null) {
  const normalized = normalizeBundlePickRow(row, offer, slotIdx);
  const prod = productsById?.get?.(String(normalized.product_id));
  const hasSizes = productSizesList(prod).length > 0;
  const selected_size = hasSizes ? normalized.selected_size : null;
  const product_name = String(
    normalized.product_name || prod?.name_ar || prod?.name || ""
  ).trim();
  const slots = normalizePickSlots(offer?.pick_slots);
  let slot_surcharge = normalized.slot_surcharge;
  if (slot_surcharge == null && offerUsesPickSlots(offer) && slotIdx != null && slots[slotIdx]) {
    slot_surcharge = slotOptionSurcharge(slots[slotIdx], normalized.product_id);
  }
  const addonCatalog = prod?.addons || prod?.product_addons || [];
  const sz = selected_size || "";
  const hasSizesForAddons = hasSizes;
  const selected_addons = normalized.selected_addons.map((ad) => {
    const name = String(ad.name || "").trim();
    const raw = addonCatalog.find((a) => String(a.id) === String(ad.id));
    const resolvedPrice =
      raw && resolveAddonPriceForSize(raw, sz, hasSizesForAddons) != null
        ? resolveAddonPriceForSize(raw, sz, hasSizesForAddons)
        : ad.price;
    return {
      ...ad,
      name: name || String(raw?.name || "").trim(),
      price: parsePrice(resolvedPrice, ad.price),
    };
  });
  return {
    ...normalized,
    selected_size,
    product_name,
    selected_addons,
    ...(slot_surcharge != null ? { slot_surcharge } : {}),
  };
}

export function validateBundlePicks(offer, picks, productsById) {
  const missing = [];
  const slots = normalizePickSlots(offer?.pick_slots);
  const usesSlots = offerUsesPickSlots(offer);
  const count = usesSlots ? slots.length : Math.max(1, Number(offer?.pick_count) || 1);
  const rows = Array.isArray(picks) ? picks : [];
  if (rows.length !== count) {
    missing.push(`اختر ${count} منتجات للعرض`);
    return missing;
  }

  if (usesSlots) {
    rows.forEach((row, idx) => {
      const slot = slots[idx];
      const label = slot?.label || `العنصر ${idx + 1}`;
      const pid = String(row?.product_id || row?.productId || "").trim();
      if (!pid) {
        missing.push(label);
        return;
      }
      const allowed = new Set((slot?.options || []).map((o) => String(o.product_id)));
      if (!allowed.has(pid)) {
        missing.push(`${label}: خيار غير صالح`);
        return;
      }
      const p = productsById.get(pid);
      const sizes = productSizesList(p);
      if (!sizes.length) return;
      const slotReq = slotOptionRequiredSize(slot, pid);
      const sz = String(row?.selected_size || row?.size || slotReq || "").trim();
      if (slotReq && sz && slotReq.toLowerCase() !== sz.toLowerCase()) {
        missing.push(`الحجم مطلوب ${slotReq} لـ ${label}`);
      }
      if (!sz) {
        missing.push(`اختر الحجم لـ ${label}`);
      }
    });
    return missing;
  }

  const reqSize = String(offer?.required_size || "").trim();
  const eligible = getEligibleProductsForOffer(offer, [...productsById.values()]);
  const eligibleIds = new Set(eligible.map((p) => String(p.id)));

  rows.forEach((row, idx) => {
    const pid = String(row?.product_id || row?.productId || "").trim();
    if (!pid) {
      missing.push(`المنتج ${idx + 1}`);
      return;
    }
    if (!eligibleIds.has(pid)) {
      missing.push(`المنتج ${idx + 1} غير مشمول بالعرض`);
      return;
    }
    const p = productsById.get(pid);
    const sizes = productSizesList(p);
    const sz = String(row?.selected_size || row?.size || reqSize || "").trim();
    if (reqSize && sz && reqSize.toLowerCase() !== sz.toLowerCase()) {
      missing.push(`الحجم مطلوب ${reqSize} للمنتج ${idx + 1}`);
    }
    if (sizes.length > 0 && !sz) {
      missing.push(`اختر الحجم للمنتج ${idx + 1}`);
    }
  });
  return missing;
}

export function formatBundlePickLabel(pick, productsById) {
  const snapName = String(pick?.product_name || pick?.product_name_ar || "").trim();
  const p = productsById?.get?.(String(pick.product_id || pick.productId || ""));
  const name = snapName || p?.name_ar || p?.name || "منتج";
  const hasSizes = productSizesList(p).length > 0;
  const sz = hasSizes ? pick.selected_size || pick.size || "" : "";
  const parts = [name];
  if (sz) parts.push(sz);
  const sur = pick.slot_surcharge;
  if (sur != null && Number(sur) > 0) parts.push(`+₪${sur}`);
  const ads = pick.selected_addons || pick.addons || [];
  if (ads.length) {
    parts.push(
      ads.map((a) => `${a.name}${a.price ? ` (+₪${a.price})` : ""}`).join("، ")
    );
  }
  return parts.join(" — ");
}

export function bundleOfferSnapshotForCart(offer) {
  return {
    name: String(offer?.name || "").trim(),
    pricing_mode: offer?.pricing_mode === "discount" ? "discount" : "fixed",
    offer_price: offer?.offer_price,
    discount_percent: offer?.discount_percent,
    pick_slots: offer?.pick_slots,
    required_size: offer?.required_size,
  };
}

/** Rebuild pricing meta for older cart lines saved before bundleOfferMeta existed. */
export function inferBundleOfferMetaFromCartItem(item) {
  if (item?.bundleOfferMeta) return item.bundleOfferMeta;
  const picks = Array.isArray(item?.bundlePicks) ? item.bundlePicks : [];
  const slotExtra = picks.reduce((sum, pick) => sum + parsePrice(pick?.slot_surcharge, 0), 0);
  const addons = computeBundleAddonsTotal(picks, new Map());
  const unit = parsePrice(item?.price, 0);
  const offerPrice = Math.max(0, Math.round((unit - slotExtra - addons) * 100) / 100);
  return {
    name: String(item?.name || "عرض").trim(),
    pricing_mode: "fixed",
    offer_price: offerPrice > 0 ? offerPrice : unit,
    pick_slots: [],
  };
}

export function bundleOfferNoteText(offer, picks, productsById) {
  const lines = [String(offer?.name || "عرض")];
  (picks || []).forEach((pick, i) => {
    lines.push(`• ${i + 1}) ${formatBundlePickLabel(pick, productsById)}`);
  });
  const base = computeBundleBasePrice(offer, picks, productsById);
  const addons = computeBundleAddonsTotal(picks, productsById);
  const slotExtra = computeBundleSlotSurcharges(offer, picks);
  lines.push(`• سعر العرض: ₪${base}`);
  if (slotExtra > 0) lines.push(`• إضافات الاختيار: ₪${slotExtra}`);
  if (addons > 0) lines.push(`• إضافات: ₪${addons}`);
  return lines.join("\n");
}

export function whatsappOrderTitleForCart(cart, { isWholesale = false } = {}) {
  if (isWholesale) return "*طلب جملة جديد*";
  const offerNames = [
    ...new Set(
      (Array.isArray(cart) ? cart : [])
        .filter((item) => item?.isBundleOffer)
        .map((item) => String(item.name || "").trim())
        .filter(Boolean)
    ),
  ];
  if (offerNames.length === 1) {
    return `*طلب جديد — عرض: ${offerNames[0]}*`;
  }
  if (offerNames.length > 1) {
    return `*طلب جديد — عروض: ${offerNames.join("، ")}*`;
  }
  return "*طلب جديد*";
}

export function bundleOfferMergeKey(sectionId, offerId, picks) {
  const parts = (picks || []).map((pick, i) => {
    const pid = String(pick.product_id || pick.productId || "");
    const sz = String(pick.selected_size || pick.size || "");
    const ads = (pick.selected_addons || pick.addons || [])
      .map((a) => `${a.id}:${a.price}`)
      .sort()
      .join(",");
    return `${i}:${pid}:${sz}:${ads}`;
  });
  return `bundle:${sectionId}:${offerId}|${parts.join("|")}`;
}

export function serializeBundlePicksForApi(picks) {
  return (picks || []).map((row) => {
    const normalized = normalizeBundlePickRow(row, {});
    const out = {
      product_id: normalized.product_id,
      selected_size: normalized.selected_size,
    };
    if (normalized.selected_addons?.length) {
      out.selected_addons = normalized.selected_addons.map((a) => ({ id: a.id }));
    }
    return out;
  });
}

export function getVisibleAddonsForProductPick(product, size) {
  if (!product?.addons_enabled) return [];
  const list = Array.isArray(product.addons)
    ? product.addons
    : Array.isArray(product.product_addons)
      ? product.product_addons
      : [];
  const sizes = Array.isArray(product.sizes) ? product.sizes : [];
  const hasSizes = sizes.length > 0;
  return list
    .map((a) => {
      const price = resolveAddonPriceForSize(a, size, hasSizes);
      if (price == null) return null;
      return { id: String(a.id), name: String(a.name || ""), price };
    })
    .filter(Boolean);
}
